
import React, { useState, useEffect } from 'react';
import { X, FileText, Download, Loader2, AlertTriangle, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { legalService } from '../services/api';
import type { NoticeTemplate } from '../services/api';
import { toast } from 'sonner';
import { safeStr } from '../utils/safeStr';
import { useCaseEditBlock } from '../case-canvas/caseAccess';

export interface NoticeTransaction {
    tx_hash?: string;
    value?: number;
    token_symbol?: string;
    timestamp?: string | Date;
    direction?: string;
    counterparty?: string;
    counterparty_label?: string;
    historical_usd?: number;
    block_height?: number;
}

export interface NoticeCounterparty {
    address: string;
    label?: string;
    entity_name?: string;
    entity_category?: string;
    risk_score?: number;
    sanctioned?: boolean;
    is_mixer?: boolean;
    is_exchange?: boolean;
    is_bridge?: boolean;
    intelligence_attributions?: Array<{ source: string; value: string; key?: string }>;
}

export interface NoticeTargetExtras {
    ens_name?: string;
    name_service?: string;
    entity_name?: string;
    entity_category?: string;
    risk_score?: number;
    risk_level?: string;
    sanctioned?: boolean;
    sanctions_program?: string;
    activity_summary?: {
        tx_count?: number;
        first_seen?: string;
        last_seen?: string;
        total_volume?: string;
    };
    forensic_records?: Array<{
        file_name?: string;
        record_type?: string;
        received_at?: string;
    }>;
    // Exchange-typing (Item 2): how the exchange wallet was identified — renders
    // the "Basis of Identification" audit block in the notice PDF.
    wallet_role?: string | null;
    attribution_method?: string | null;
    attribution_tier?: string | null;
    is_offramp_point?: boolean;
}

interface NoticeModalProps {
    isOpen: boolean;
    onClose: () => void;
    exchangeName: string;
    targetAddress: string;
    chain: string;
    /** All selected transactions (multi-tx support) */
    selectedTransactions?: NoticeTransaction[];
    /** Connected counterparty nodes the user may optionally include */
    relatedNodes?: NoticeCounterparty[];
    /** Legacy single-tx props (backward compat) */
    targetTxHash?: string;
    targetAmount?: string;
    targetDate?: string;
    initialCaseContext?: {
        firNo?: string;
        firDate?: string;
        sections?: string;
        officerName?: string;
        designation?: string;
        policeStation?: string;
        officerEmail?: string;
    };
    /** Rich target metadata pulled from selectedNode — ENS, entity, activity
     *  summary, forensic-record cross-reference, etc. Renders into the new
     *  base template's enriched Suspect Wallet block. */
    targetExtras?: NoticeTargetExtras;
    /** When set, the generated notice is persisted to the case_evidence
     *  locker as a notice record (lifecycle = drafted → sent → responded). */
    caseId?: string;
    /** Optional investigation context tied to this notice. */
    investigationId?: string;
}

const formatDate = (d: string | Date | undefined): string => {
    if (!d) return 'N/A';
    try {
        const date = typeof d === 'string' ? new Date(d) : d;
        return date.toISOString().split('T')[0];
    } catch {
        return String(d);
    }
};

const NoticeModal: React.FC<NoticeModalProps> = ({
    isOpen,
    onClose,
    exchangeName,
    targetAddress,
    chain,
    selectedTransactions = [],
    relatedNodes = [],
    targetTxHash = "N/A",
    targetAmount = "N/A",
    targetDate = new Date().toISOString().split('T')[0],
    initialCaseContext,
    targetExtras,
    caseId,
    investigationId,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    // A notice on a case is stored as case evidence: a viewer collaborator may not generate it (ACC03).
    const { blocked: caseBlocked, reason: caseBlockedReason } = useCaseEditBlock(isOpen ? caseId : undefined);
    const [showPreview, setShowPreview] = useState(true);
    const [includedTxs, setIncludedTxs] = useState<NoticeTransaction[]>(selectedTransactions);
    const [includedNodes, setIncludedNodes] = useState<NoticeCounterparty[]>([]);
    const [showRelatedNodes, setShowRelatedNodes] = useState(false);

    // Template registry: fetched once per (open, exchangeName) tuple.
    const [templates, setTemplates] = useState<NoticeTemplate[]>([]);
    const [templateKey, setTemplateKey] = useState<string>('section91');

    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;
        legalService.listTemplates(exchangeName)
            .then(res => {
                if (cancelled) return;
                setTemplates(res.templates);
                const def = res.templates.find(t => t.is_default) || res.templates[0];
                if (def) setTemplateKey(def.key);
            })
            .catch(err => {
                if (cancelled) return;
                console.error('Failed to load notice templates:', err);
                // Hard fallback so the modal still works if the registry endpoint fails.
                setTemplates([
                    { key: 'section91', file: 'section91.html', label: 'Section 91 CrPC', description: '', jurisdiction: 'IN', exchange: null, is_default: true },
                    { key: 'section94', file: 'section94.html', label: 'Section 94 CrPC', description: '', jurisdiction: 'IN', exchange: null, is_default: false },
                ]);
            });
        return () => { cancelled = true; };
    }, [isOpen, exchangeName]);

    // Sync when selectedTransactions changes
    React.useEffect(() => {
        setIncludedTxs(selectedTransactions);
    }, [selectedTransactions]);

    // Form State
    const [formData, setFormData] = useState({
        firNo: '',
        firDate: '',
        sections: '419/420 IPC, 66D IT Act',
        officerName: '',
        designation: 'Inspector of Police',
        policeStation: '',
        officerEmail: ''
    });

    React.useEffect(() => {
        if (!initialCaseContext) return;
        setFormData(prev => ({
            firNo: prev.firNo || initialCaseContext.firNo || '',
            firDate: prev.firDate || initialCaseContext.firDate || '',
            sections: prev.sections || initialCaseContext.sections || '419/420 IPC, 66D IT Act',
            officerName: prev.officerName || initialCaseContext.officerName || '',
            designation: prev.designation || initialCaseContext.designation || 'Inspector of Police',
            policeStation: prev.policeStation || initialCaseContext.policeStation || '',
            officerEmail: prev.officerEmail || initialCaseContext.officerEmail || ''
        }));
    }, [initialCaseContext]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const removeTx = (index: number) => {
        setIncludedTxs(prev => prev.filter((_, i) => i !== index));
    };

    const toggleRelatedNode = (node: NoticeCounterparty) => {
        setIncludedNodes(prev => {
            const exists = prev.some(n => n.address === node.address);
            if (exists) return prev.filter(n => n.address !== node.address);
            return [...prev, node];
        });
    };

    const handleGenerate = async () => {
        setIsLoading(true);
        try {
            // Build transactions array for backend.
            // Send raw value + token_symbol separately so the template can
            // render USD column and money formatting independently. We keep
            // the legacy `amount` string as a fallback for old templates.
            const transactions = includedTxs.length > 0
                ? includedTxs.map(tx => ({
                    tx_hash: tx.tx_hash || 'N/A',
                    amount: tx.value ?? null,
                    token_symbol: safeStr(tx.token_symbol) || null,
                    historical_usd: tx.historical_usd ?? null,
                    block_height: tx.block_height ?? null,
                    date: formatDate(tx.timestamp),
                    direction: tx.direction || 'N/A',
                    counterparty: tx.counterparty || null,
                    counterparty_label: tx.counterparty_label || null,
                }))
                : [{ tx_hash: targetTxHash, amount: targetAmount, date: targetDate, direction: 'N/A', counterparty: null, counterparty_label: null }];

            // Build counterparties array — pass intel attributions through so
            // the template can show "Tornado Cash (OFAC: CYBER2)" instead of
            // bare "high risk".
            const counterparties = includedNodes.map(n => ({
                address: n.address,
                label: n.entity_name || n.label || 'Unknown',
                category: n.entity_category || 'unknown',
                risk_score: n.risk_score ?? 0,
                sanctioned: n.sanctioned || false,
                is_mixer: n.is_mixer || false,
                is_exchange: n.is_exchange || false,
                is_bridge: n.is_bridge || false,
                intelligence_attributions: n.intelligence_attributions || [],
            }));

            const payload = {
                exchange_name: exchangeName,
                case_context: {
                    fir_no: formData.firNo,
                    fir_date: formData.firDate,
                    sections: formData.sections,
                    officer_name: formData.officerName,
                    designation: formData.designation,
                    police_station: formData.policeStation,
                    officer_email: formData.officerEmail
                },
                target_context: {
                    wallet_address: targetAddress,
                    chain: chain,
                    // Legacy single-tx fields (first tx or fallback)
                    tx_hash: transactions[0]?.tx_hash || targetTxHash,
                    date: transactions[0]?.date || targetDate,
                    amount: transactions[0]?.amount || targetAmount,
                    // Rich Suspect-Wallet metadata (templates render-skip if absent)
                    ens_name: targetExtras?.ens_name,
                    name_service: targetExtras?.name_service,
                    entity_name: targetExtras?.entity_name,
                    entity_category: targetExtras?.entity_category,
                    risk_score: targetExtras?.risk_score,
                    risk_level: targetExtras?.risk_level,
                    sanctioned: targetExtras?.sanctioned,
                    sanctions_program: targetExtras?.sanctions_program,
                    activity_summary: targetExtras?.activity_summary,
                    forensic_records: targetExtras?.forensic_records,
                    // Exchange-typing (Item 2): how the exchange wallet was identified —
                    // drives the "Basis of Identification" audit block in the notice PDF.
                    wallet_role: targetExtras?.wallet_role,
                    attribution_method: targetExtras?.attribution_method,
                    attribution_tier: targetExtras?.attribution_tier,
                    is_offramp_point: targetExtras?.is_offramp_point,
                    // Multi-tx + multi-counterparty
                    transactions: transactions,
                    counterparties: counterparties
                },
                template_key: templateKey,
                case_id: caseId,
                investigation_id: investigationId,
            };

            const { blob, evidenceId } = await legalService.generateNotice(payload);

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Notice_${templateKey}_${exchangeName}_${formData.firNo || 'Draft'}.pdf`.replace(/\s+/g, '_');
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);

            if (evidenceId) {
                toast.success(`Notice saved to case evidence locker (#${evidenceId}).`);
            } else if (caseId) {
                toast.warning('PDF downloaded but persistence to evidence locker failed — check backend logs.');
            }
            onClose();
        } catch (error) {
            console.error('Error generating notice:', error);
            toast.error('Failed to generate notice. Please check console.');
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    const hasTxs = includedTxs.length > 0;
    const hasCounterparties = includedNodes.length > 0;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-card rounded-xl shadow-xl w-full max-w-2xl border border-border flex flex-col max-h-[90vh]">

                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-border">
                    <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-primary" />
                        <h2 className="text-lg font-semibold text-foreground">
                            {(templates.find(t => t.key === templateKey)?.label) || 'Generate Notice'}
                        </h2>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded hover:bg-accent text-muted-foreground hover:text-foreground">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 overflow-y-auto flex-1 space-y-6">

                    {/* ═══════ PREVIEW SUMMARY ═══════ */}
                    <div className="border border-primary/20 rounded-lg overflow-hidden">
                        <button
                            onClick={() => setShowPreview(!showPreview)}
                            className="w-full flex items-center justify-between p-3 bg-primary/5 hover:bg-primary/10 transition-colors"
                        >
                            <span className="text-sm font-semibold text-primary">
                                Notice Preview — What will be included
                            </span>
                            {showPreview ? <ChevronUp className="w-4 h-4 text-primary" /> : <ChevronDown className="w-4 h-4 text-primary" />}
                        </button>

                        {showPreview && (
                            <div className="p-4 space-y-3 bg-card">
                                {/* Target Info */}
                                <div className="text-xs space-y-1">
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground dark:text-muted-foreground">Target Exchange:</span>
                                        <span className="font-semibold text-slate-900 text-foreground">{exchangeName}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground dark:text-muted-foreground">Wallet Address:</span>
                                        <span className="font-mono text-[10px] text-slate-900 text-foreground">{targetAddress}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground dark:text-muted-foreground">Chain:</span>
                                        <span className="text-slate-900 text-foreground">{chain}</span>
                                    </div>
                                </div>

                                {/* Warning if no transactions */}
                                {!hasTxs && (
                                    <div className="flex items-start gap-2 p-2 bg-amber-500/10 border border-amber-500/30 rounded text-xs">
                                        <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-500 flex-shrink-0 mt-0.5" />
                                        <div>
                                            <p className="font-semibold text-amber-700 dark:text-amber-300">No specific transactions selected</p>
                                            <p className="text-amber-600 dark:text-amber-400 mt-0.5">
                                                The notice will request <strong>general records</strong> for this wallet.
                                                For a stronger notice, go back and select specific transactions from the Txs tab first.
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* Transaction Table */}
                                {hasTxs && (
                                    <div>
                                        <div className="text-xs font-semibold text-slate-700 dark:text-foreground/80 mb-1">
                                            Transactions to include ({includedTxs.length}):
                                        </div>
                                        <div className="max-h-32 overflow-y-auto border border-border rounded">
                                            <table className="w-full text-[10px]">
                                                <thead className="bg-slate-50 dark:bg-muted sticky top-0">
                                                    <tr>
                                                        <th className="text-left px-2 py-1 text-slate-700 dark:text-foreground/80">Tx Hash</th>
                                                        <th className="text-right px-2 py-1 text-slate-700 dark:text-foreground/80">Amount</th>
                                                        <th className="text-right px-2 py-1 text-slate-700 dark:text-foreground/80">Date</th>
                                                        <th className="w-6"></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {includedTxs.map((tx, i) => (
                                                        <tr key={i} className="border-t border-border hover:bg-muted/50">
                                                            <td className="px-2 py-1 font-mono truncate max-w-[120px] text-slate-900 text-foreground">
                                                                {tx.tx_hash ? `${tx.tx_hash.slice(0, 10)}...` : 'N/A'}
                                                            </td>
                                                            <td className="px-2 py-1 text-right whitespace-nowrap text-slate-900 text-foreground">
                                                                {tx.value?.toLocaleString() || '?'} {safeStr(tx.token_symbol) || ''}
                                                            </td>
                                                            <td className="px-2 py-1 text-right whitespace-nowrap text-slate-900 text-foreground">
                                                                {formatDate(tx.timestamp)}
                                                            </td>
                                                            <td className="px-1 py-1">
                                                                <button onClick={() => removeTx(i)} className="text-red-400 hover:text-red-600" title="Remove from notice">
                                                                    <Trash2 className="w-3 h-3" />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {/* Related Counterparties (optional inclusion) */}
                                {relatedNodes.length > 0 && (
                                    <div>
                                        <button
                                            onClick={() => setShowRelatedNodes(!showRelatedNodes)}
                                            className="flex items-center gap-1 text-xs font-semibold text-foreground/80 hover:text-primary"
                                        >
                                            {showRelatedNodes ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                            Include counterparty addresses? ({includedNodes.length}/{relatedNodes.length} selected)
                                        </button>
                                        {showRelatedNodes && (
                                            <div className="mt-1 max-h-28 overflow-y-auto border border-border rounded p-1 space-y-0.5">
                                                {relatedNodes.map((node, i) => {
                                                    const isIncluded = includedNodes.some(n => n.address === node.address);
                                                    return (
                                                        <label key={i} className="flex items-center gap-2 px-2 py-1 hover:bg-muted/50 rounded cursor-pointer text-[10px]">
                                                            <input type="checkbox" checked={isIncluded} onChange={() => toggleRelatedNode(node)} className="rounded border-border" />
                                                            <span className="font-mono truncate max-w-[140px] text-slate-900 text-foreground">{node.address.slice(0, 12)}...</span>
                                                            <span className="text-muted-foreground dark:text-muted-foreground">{safeStr(node.entity_name) || safeStr(node.label) || '?'}</span>
                                                            {node.entity_category && (
                                                                <span className={`px-1 rounded ${node.entity_category === 'scam' || node.entity_category === 'sanction' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' : 'bg-slate-100 text-muted-foreground/80 dark:bg-muted dark:text-muted-foreground'}`}>
                                                                    {node.entity_category}
                                                                </span>
                                                            )}
                                                            {node.risk_score !== undefined && (
                                                                <span className={`ml-auto ${(node.risk_score || 0) > 0.7 ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground dark:text-muted-foreground'}`}>
                                                                    {Math.round((node.risk_score || 0) * 100)}%
                                                                </span>
                                                            )}
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Summary line */}
                                <div className="text-[10px] text-muted-foreground dark:text-muted-foreground border-t border-border pt-2">
                                    This notice will request KYC, IP logs, and transaction history for the above wallet
                                    {hasTxs && `, citing ${includedTxs.length} specific transaction(s) as evidence`}
                                    {hasCounterparties && `, mentioning ${includedNodes.length} related counterparty address(es)`}
                                    .
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ═══════ CASE DETAILS FORM ═══════ */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-700 dark:text-foreground/80">Notice Template</label>
                        <select
                            className="flex h-10 w-full rounded-md border border-border bg-background text-foreground px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                            value={templateKey}
                            onChange={(e) => setTemplateKey(e.target.value)}
                            disabled={templates.length === 0}
                        >
                            {templates.map(t => (
                                <option key={t.key} value={t.key}>
                                    {t.label}{t.is_default ? ' — default for this exchange' : ''}
                                </option>
                            ))}
                        </select>
                        {templates.find(t => t.key === templateKey)?.description && (
                            <p className="text-[11px] text-muted-foreground">
                                {templates.find(t => t.key === templateKey)?.description}
                            </p>
                        )}
                        {!caseId && (
                            <p className="text-[11px] text-amber-600 dark:text-amber-400">
                                Notice will be generated and downloaded but NOT saved to a case evidence locker
                                (open this from inside a case to enable lifecycle tracking).
                            </p>
                        )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-slate-700 dark:text-foreground/80">FIR Number</label>
                            <input className="flex h-10 w-full rounded-md border border-border bg-background text-foreground px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary" name="firNo" placeholder="e.g. 123/2026" value={formData.firNo} onChange={handleChange} />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-slate-700 dark:text-foreground/80">FIR Date</label>
                            <input className="flex h-10 w-full rounded-md border border-border bg-background text-foreground px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary" name="firDate" type="date" value={formData.firDate} onChange={handleChange} />
                        </div>
                        <div className="col-span-2 space-y-2">
                            <label className="text-sm font-medium text-slate-700 dark:text-foreground/80">Relevant Sections</label>
                            <input className="flex h-10 w-full rounded-md border border-border bg-background text-foreground px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary" name="sections" value={formData.sections} onChange={handleChange} />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-slate-700 dark:text-foreground/80">Reviewing Officer Name</label>
                            <input className="flex h-10 w-full rounded-md border border-border bg-background text-foreground px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary" name="officerName" placeholder="Name" value={formData.officerName} onChange={handleChange} />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-slate-700 dark:text-foreground/80">Designation</label>
                            <input className="flex h-10 w-full rounded-md border border-border bg-background text-foreground px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary" name="designation" placeholder="Rank" value={formData.designation} onChange={handleChange} />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-slate-700 dark:text-foreground/80">Police Station / Unit</label>
                            <input className="flex h-10 w-full rounded-md border border-border bg-background text-foreground px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary" name="policeStation" placeholder="Cyber Crime Cell, City" value={formData.policeStation} onChange={handleChange} />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-slate-700 dark:text-foreground/80">Official Email (for reply)</label>
                            <input className="flex h-10 w-full rounded-md border border-border bg-background text-foreground px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary" name="officerEmail" placeholder="officer@keralapolice.gov.in" value={formData.officerEmail} onChange={handleChange} />
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-border flex justify-between items-center bg-muted/20">
                    <div className="text-[10px] text-muted-foreground max-w-[280px]">
                        {hasTxs
                            ? `${includedTxs.length} transaction(s) will appear in the notice.`
                            : 'General records request — no specific transactions cited.'}
                        {hasCounterparties && ` ${includedNodes.length} counterparty address(es) included.`}
                    </div>
                    <div className="flex gap-3">
                        <button className="px-4 py-2 border border-border rounded-md text-sm font-medium text-foreground hover:bg-accent" onClick={onClose}>Cancel</button>
                        <button onClick={handleGenerate} disabled={isLoading || caseBlocked} title={caseBlocked ? caseBlockedReason : undefined} data-testid="notice-generate" className="flex items-center justify-center px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:opacity-90 disabled:opacity-50 shadow-sm">
                            {isLoading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</>) : (<><Download className="w-4 h-4 mr-2" />Generate PDF Notice</>)}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NoticeModal;
